import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./NonFarmLivelihood.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

/* ---- Simple inline icons (no external icon-font dependency) ---- */
const IconLeaf = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M5 21c8 0 14-6 14-14V5h-2C9 5 3 11 3 19v2h2z" />
    <path d="M5 21c4-6 8-9 14-12" />
  </svg>
);

const IconTools = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M14.5 6.5l3 3L8 19l-4 1 1-4 9.5-9.5z" />
    <path d="M13 8l3 3" />
    <path d="M17.5 3.5l3 3-2 2-3-3z" />
  </svg>
);

const IconCow = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M4 10c0-2.2 1.8-4 4-4h8c2.2 0 4 1.8 4 4" />
    <path d="M3 10l1.5-3M21 10l-1.5-3" />
    <path d="M5 10v5c0 2.8 2.2 5 5 5h4c2.8 0 5-2.2 5-5v-5" />
    <circle cx="9.5" cy="12" r="0.6" fill="#1f9d55" />
    <circle cx="14.5" cy="12" r="0.6" fill="#1f9d55" />
    <path d="M10 15h4" />
  </svg>
);

const IconLink = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M9 15l6-6" />
    <path d="M11 6l1.5-1.5a3.5 3.5 0 015 5L16 11" />
    <path d="M13 18l-1.5 1.5a3.5 3.5 0 01-5-5L8 13" />
  </svg>
);

const IconSprout = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M12 22v-9" />
    <path d="M12 13c0-4 3-6 7-6 0 4-3 6-7 6z" />
    <path d="M12 13C12 9 9 7 5 7c0 4 3 6 7 6z" />
  </svg>
);

const IconWomen = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <circle cx="12" cy="7" r="3.2" />
    <path d="M12 10.2V16" />
    <path d="M8.5 13.5h7" />
    <path d="M12 16l-2.5 5M12 16l2.5 5" />
  </svg>
);

const IconArrowRight = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

const focusAreas = [
  {
    icon: <IconTools />,
    title: "Skill Development",
    text: "Training artisans and rural youth in market-oriented skills.",
  },
  {
    icon: <IconCow />,
    title: "Livestock Support",
    text: "Promoting livestock rearing and allied enterprises.",
  },
  {
    icon: <IconLink />,
    title: "Market Linkages",
    text: "Connecting producers with markets and value chains.",
  },
  {
    icon: <IconSprout />,
    title: "Entrepreneurship",
    text: "Encouraging rural entrepreneurs and small businesses.",
  },
  {
    icon: <IconWomen />,
    title: "Women Empowerment",
    text: "Building capacity and creating opportunities for women.",
  },
];

// Renamed component for clarity
const NonFarmLivelihood = () => {
  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="page-header page-header--nfbl">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/sections/NFBL.jpg)", // Make sure this path is correct
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Non-Farm Based Livelihood
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Supporting communities by creating diverse economic
              opportunities beyond traditional agriculture.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="py-5 nfl-mission-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center justify-content-center g-5">
              <motion.div className="col-md-5 order-md-2" variants={staggerItem}>
                <span className="nfl-eyebrow">
                  <IconLeaf /> Our Mission
                </span>
                <h2 className="fw-bold mb-2" style={{ color: "#006633" }}>
                  Expanding Economic
                  <br />
                  Horizons
                </h2>
                <div className="nfl-heading-underline"></div>
                <p className="lh-lg">
                  We promote artisan development, livestock growth, and
                  rural businesses across multiple districts in Odisha. By
                  providing training, resources, and market linkages, we
                  help create sustainable non-farm income streams for
                  marginalized communities.
                </p>
              </motion.div>
              <motion.div className="col-md-5 order-md-1" variants={staggerItem}>
                <div className="nfl-image-wrap">
                  <span className="nfl-image-leaf">
                    <IconLeaf />
                  </span>
                  <Row className="g-3">
                    <Col xs={6}>
                      <img
                        src="/assets/images/Subsections/nf1.jpg"
                        alt="Artisan at work"
                        className="img-fluid rounded-5 shadow"
                      />
                    </Col>
                    <Col xs={6}>
                      <img
                        src="/assets/images/Subsections/nf2.jpg"
                        alt="Rural business"
                        className="img-fluid rounded-5 shadow nfl-image--offset"
                      />
                    </Col>
                  </Row>
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Key Areas of Focus Section */}
      <section className="py-5 bg-light nfl-focus-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center mb-5"
          >
            <span className="nfl-eyebrow nfl-eyebrow--center">
              <IconLeaf /> What We Do
            </span>
            <h2 className="fw-bold">
              Our Key Areas of <span className="text-success">Focus</span>
            </h2>
            <p className="lead nfl-subtext">
              We have implemented diverse programs to develop local
              economies.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {focusAreas.map((area, index) => (
                <Col md={4} lg={true} key={index} className="nfl-focus-col">
                  <motion.div variants={staggerItem} className="h-100">
                    <Card className="text-center p-3 border-0 shadow-sm h-100 nfl-focus-card">
                      <div className="nfl-focus-icon">{area.icon}</div>
                      <h6>{area.title}</h6>
                      <p>{area.text}</p>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Strip */}
      <section className="nfl-cta-section">
        <Container fluid className="px-0">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="nfl-cta-box">
              <Container>
                <Row className="align-items-center g-4">
                  <Col lg={7}>
                    <div className="nfl-cta-heading">
                      <span className="nfl-cta-icon">
                        <IconLeaf />
                      </span>
                      <h3>
                        Together, We Build
                        <br />
                        Stronger Communities
                      </h3>
                    </div>
                  </Col>
                  <Col lg={5}>
                    <div className="nfl-cta-right">
                      <p>
                        Join us in creating sustainable livelihoods and a
                        better future for rural communities.
                      </p>
                      <Button className="nfl-btn">
                        Get Involved <IconArrowRight />
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Container>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default NonFarmLivelihood;